<template>
    <div class="m-container py-8">
        <!-- Header -->
        <div class="m-row mb-8" style="align-items: center;">
            <div class="m-col">
                <h1 class="titel-h1 font-weight-bold text-secondary">
                    Alle Kategorien vergleichen
                </h1>
                <p class="text-body-2 text-medium-emphasis mt-1">
                    Entdecke unsere vollständige Produktpalette nach Kategorien
                    geordnet
                </p>
            </div>
            <div class="m-col text-right" style="flex: 0 0 auto;">
                <div class="text-h4 font-weight-bold text-primary">
                    {{ categoriesStore.totalCategories }}
                </div>
                <div class="text-caption text-grey-darken-1">
                    Kategorien verfügbar
                </div>
            </div>
        </div>

        <!-- Error State -->
        <MAlert
            v-if="categoriesStore.error"
            type="error"
            variant="tonal"
            class="mb-6"
        >
            <div class="d-flex align-center justify-space-between">
                <span>{{ categoriesStore.error }}</span>
                <MBtn
                    variant="text"
                    color="error"
                    size="small"
                    @click="categoriesStore.fetchCategories()"
                    class="text-none"
                >
                    Erneut versuchen
                </MBtn>
            </div>
        </MAlert>

        <!-- Skeleton Loading -->
        <div class="m-row" v-if="categoriesStore.loading">
            <div v-for="n in 12" :key="n" class="m-col m-col-12 m-col-sm-6 m-col-md-4 m-col-lg-3">
                <MSkeletonLoader type="image, heading, text@3" />
            </div>
        </div>

        <!-- Categories Grid -->
        <div class="m-row" v-else-if="categoriesStore.categories.length > 0">
            <div
                v-for="category in categoriesStore.categories"
                :key="category.id"
                class="m-col m-col-12 m-col-sm-6 m-col-md-4 m-col-lg-3"
            >
                <MCard hover class="h-100 cat-card" :to="getCatPath(category)">
                    <!-- Header: Brand-Gradient mit Icon -->
                    <div
                        class="cat-card__header"
                        :style="getHeaderStyle(category.name)"
                    >
                        <div class="cat-card__icon-wrap">
                            <MIcon
                                :icon="getCategoryIcon(category.name)"
                                :size="36"
                                color="white"
                            />
                        </div>
                        <div class="cat-card__title-block">
                            <div class="cat-card__title">
                                {{ category.name }}
                            </div>
                            <div class="cat-card__count">
                                {{ category.children?.length || 0 }} Unterkategorien
                            </div>
                        </div>
                    </div>

                    <!-- Beschreibung -->
                    <MCardText class="cat-card__description">
                        <p class="text-body-2 text-medium-emphasis mb-0">
                            {{ getCategoryDescription(category) }}
                        </p>
                    </MCardText>

                    <hr class="m-divider" />

                    <!-- Subkategorien-Liste -->
                    <MCardText
                        v-if="category.children && category.children.length > 0"
                        class="pt-2"
                    >
                        <div class="d-flex flex-wrap ga-1">
                            <MChip
                                v-for="sub in category.children.slice(0, 5)"
                                :key="sub.id"
                                :to="getCatPath(sub)"
                                size="small"
                                variant="tonal"
                                color="primary"
                                class="cat-card__chip"
                            >
                                {{ sub.name }}
                            </MChip>
                            <MChip
                                v-if="category.children.length > 5"
                                :to="getCatPath(category)"
                                size="small"
                                variant="text"
                                color="primary"
                            >
                                +{{ category.children.length - 5 }} weitere
                            </MChip>
                        </div>
                    </MCardText>
                </MCard>
            </div>
        </div>

        <!-- Empty State -->
        <MCard
            v-else-if="
                !categoriesStore.loading &&
                categoriesStore.categories.length === 0
            "
            variant="flat"
            class="text-center py-12"
        >
            <MIcon
                icon="package-variant"
                :size="64"
                color="grey-lighten-1"
                class="mb-4"
            />
            <h3 class="text-h6 font-weight-medium mb-2">
                Keine Kategorien gefunden
            </h3>
            <p class="text-body-2 text-grey-darken-1">
                Aktuell sind keine Produktkategorien verfügbar.
            </p>
        </MCard>

        <!-- Footer Stats -->
        <MSheet
            v-if="categoriesStore.categories.length > 0"
            color="grey-lighten-4"
            rounded
            class="text-center pa-4 mt-8"
        >
            <p class="text-body-2 text-grey-darken-1 mb-0">
                Insgesamt
                <span class="font-weight-bold text-primary">{{
                    categoriesStore.categoriesWithChildren
                }}</span>
                Haupt-Kategorien mit
                <span class="font-weight-bold text-primary">{{
                    categoriesStore.totalSubCategories
                }}</span>
                Unterkategorien
            </p>
        </MSheet>
    </div>
</template>

<script setup lang="ts">
import { useCategoriesStore, type CategoryItem } from "~/stores/categories";

const categoryIcons: Record<string, string> = {
    Büro: "briefcase-variant",
    Bad: "shower",
    Garten: "tree",
    Baumarkt: "hammer-wrench",
    Deko: "flower-tulip",
    Essen: "silverware-fork-knife",
    Flur: "door",
    Kinder: "teddy-bear",
    Schlafen: "bed",
    Leuchten: "lightbulb-on",
    Textilien: "tshirt-crew",
    Wohnen: "sofa",
};

// Alle Karten in derselben Brand-Teal-Optik wie "Bad" — kein Farbmischen,
// einheitlich, wirkt als zusammenhängendes System.
const TEAL_GRADIENT = "linear-gradient(135deg, #3c8f93 0%, #5dbab3 100%)";
const categoryGradients: Record<string, string> = {
    Büro:      TEAL_GRADIENT,
    Bad:       TEAL_GRADIENT,
    Garten:    TEAL_GRADIENT,
    Baumarkt:  TEAL_GRADIENT,
    Deko:      TEAL_GRADIENT,
    Essen:     TEAL_GRADIENT,
    Flur:      TEAL_GRADIENT,
    Kinder:    TEAL_GRADIENT,
    Schlafen:  TEAL_GRADIENT,
    Leuchten:  TEAL_GRADIENT,
    Textilien: TEAL_GRADIENT,
    Wohnen:    TEAL_GRADIENT,
};

const getHeaderStyle = (name: string) => ({
    background:
        categoryGradients[name] ||
        "linear-gradient(135deg, #3c8f93 0%, #e34444 100%)",
});

const categoryFallbackDescriptions: Record<string, string> = {
    Büro: "Schreibtische, Stühle, Regale & alles fürs produktive Arbeitszimmer im Vergleich.",
    Bad: "Badmöbel, Spiegel, Sanitär — Wellness und Funktion fürs Badezimmer.",
    Garten: "Outdoor-Möbel, Pflanzgefäße, Garten-Deko für Balkon und Terrasse.",
    Baumarkt: "Werkzeuge, Heizung, Sanitär & Material für Heimwerker und Renovierung.",
    Deko: "Vasen, Bilder, Skulpturen — Wohn-Akzente die Räume zum Leben erwecken.",
    Essen: "Küchengeschirr, Besteck, Esstische, Stühle — alles rund ums Essen.",
    Flur: "Garderoben, Schuhschränke, Spiegel — der erste Eindruck im Eingangsbereich.",
    Kinder: "Kinderbetten, Schreibtische, Spielmöbel & kindgerechte Aufbewahrung.",
    Schlafen: "Betten, Matratzen, Schränke — erholsamer Schlaf beginnt mit dem richtigen Möbel.",
    Leuchten: "Decken-, Wand-, Tisch- und Bodenleuchten für jedes Zimmer.",
    Textilien: "Vorhänge, Kissen, Teppiche & Bettwäsche — weiche Akzente fürs Zuhause.",
    Wohnen: "Sofas, Sessel, Tische, Schränke — das Herzstück deines Wohnzimmers.",
};

const getCategoryIcon = (name: string): string =>
    categoryIcons[name] || "shape-outline";
const getCategoryDescription = (cat: CategoryItem): string =>
    cat.titleDescription ||
    categoryFallbackDescriptions[cat.name] ||
    "Entdecke alle Produkte in dieser Kategorie.";

const categoriesStore = useCategoriesStore();

const getCatPath = (cat: CategoryItem) =>
    "/" + categoriesStore.getCategoryPath(cat);

onMounted(() => {
    categoriesStore.fetchCategories();
});

useSeoMeta({
    title: "Alle Kategorien",
    description:
        "Entdecke unsere vollständige Produktpalette. Alle Möbelkategorien übersichtlich geordnet — von Sofas bis Leuchten.",
    ogTitle: "Alle Kategorien — Moebix.de",
    ogDescription:
        "Entdecke unsere vollständige Produktpalette. Alle Möbelkategorien übersichtlich geordnet — von Sofas bis Leuchten.",
    ogType: "website",
    ogImage: "/moebix_og_sticker.jpg",
    ogUrl: "https://www.moebix.de/kategorien",
});

useHead({
    link: [{ rel: "canonical", href: "https://www.moebix.de/kategorien" }],
});
</script>

<style scoped>
.cat-card {
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.cat-card:hover {
    transform: translateY(-2px);
}

.cat-card__header {
    position: relative;
    height: 120px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    overflow: hidden;
}
.cat-card__header::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
        radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.18), transparent 50%),
        radial-gradient(circle at 80% 80%, rgba(0, 0, 0, 0.15), transparent 50%);
    pointer-events: none;
}
.cat-card__icon-wrap {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: var(--m-radius);
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
}
.cat-card__title-block {
    color: white;
    min-width: 0;
}
.cat-card__title {
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.2;
}
.cat-card__count {
    font-size: 0.75rem;
    opacity: 0.85;
    margin-top: 2px;
}

.cat-card__description {
    padding: 12px 16px 8px;
    min-height: 64px;
}

.cat-card__chip {
    font-weight: 500;
}
</style>
